import React, { useCallback, useMemo, useRef, useState } from 'react';
import {
	FormItem,
	Button,
	Space,
	Modal,
	ModalTitle,
	Textbox,
	DayPickerInput,
	Player,
	IconButton,
} from '@components';
import './CreateMeetingModal.scss';
import { useDropzone } from 'react-dropzone';
import { NewMeetingInfo } from '../MeetingModals/NewMeetingInfo';
import { GUID, useClassNames } from '@common';
import moment from 'moment';
import { newMeetingInfoValidator, ValMeetingInfo } from '@modals';
import { AcceptIcon, DeleteHoverIcon, MeetingPauseRecIconModal, RecordIcon, UploadIcon } from '@icons';

type CreationMode = 'upload' | 'record';

interface CreateMeetingModalProps {
	mode: CreationMode,
	closeModal: () => void,
	onSubmit: (newMeetingInfo: NewMeetingInfo) => Promise<void>,
}

function CreateMeetingModal(props: CreateMeetingModalProps): JSX.Element {
	const {
		mode,
		onSubmit,
		closeModal,
	} = props;

	const [meetingSubject, setMeetingSubject] = useState<string>('');
	const [valMessages, setValMessages] = useState({});
	const [isUploading, setIsUploading] = useState(false);
	
	const [meetingLocation, setMeetingLocation] = useState<string>('');
	const [recordingFile, setRecordingFile] = useState<File>(null);
	const [recordingFileUri, setRecordingFileUri] = useState<string>(null);
	const [isRecordingFromFile, setIsRecordingFromFile] = useState<boolean>(false);
	const [meetingStartDate, setMeetingStartDate] = useState(new Date());
	const [meetingStartTime, setMeetingStartTime] = useState(`${new Date().getHours()}:00`);
	const [meetingEndTime, setMeetingEndTime] = useState(`${new Date().getHours()}:30`);

	const [recorder, setRecorder] = useState(null as MediaRecorder);
	const [isRecording, setIsRecording] = useState<boolean>(false);
	const [isRecordingPaused, setIsRecordingPaused] = useState<boolean>(false);
	

	const filePlayerRef = useRef<HTMLAudioElement>(null);
	const recordingVisRef = useRef<HTMLCanvasElement>(null);

	const isPlannedRecording = mode == 'record';

	const getFormData = useCallback(() => {
		const newMeetingInfo = new ValMeetingInfo();
		newMeetingInfo.id = GUID();
		newMeetingInfo.subject = meetingSubject;

		newMeetingInfo.recordingFileAndIsPlanned = [recordingFile, isPlannedRecording];
		newMeetingInfo.isPlannedRecording = isPlannedRecording;
		newMeetingInfo.recordingFile = recordingFile;
		newMeetingInfo.location = meetingLocation;

		newMeetingInfo.startDateString = meetingStartDate;
		newMeetingInfo.startTimeString = meetingStartTime;
		newMeetingInfo.endTimeString = meetingEndTime;
		newMeetingInfo.startAndEndTimes = [meetingStartTime, meetingEndTime, meetingStartDate];

		newMeetingInfo.plannedAgentRegId = null;
		newMeetingInfo.plannedAgentSeetings = null;

		return newMeetingInfo;

	}, [meetingSubject, recordingFile, meetingLocation,
		meetingStartDate, meetingStartTime, meetingEndTime,
		isPlannedRecording]);

	const validateForm = useCallback((formData): boolean => {
		let result = false;

		const callback = (errors, fields) => {
			if (errors) {
				setValMessages(fields);
				result = false;
			} else {
				setValMessages({});
				result = true;
			}
		};

		newMeetingInfoValidator.validate(formData, {}, callback);

		return result;
	}, [setValMessages]);

	const handleSubmit = useCallback(async () => {
		const formData = getFormData();
		const valResult = validateForm(formData);

		if (valResult) {
			const newMeetingInfo = new NewMeetingInfo();
			newMeetingInfo.id = formData.id;
			newMeetingInfo.subject = formData.subject;
			newMeetingInfo.recordingFile = formData.recordingFile;
			newMeetingInfo.location = formData.location;

			const startHours = formData.startTimeString.split(':')[0];
			const startMinutes = formData.startTimeString.split(':')[1];

			const endHours = formData.endTimeString.split(':')[0];
			const endMinutes = formData.endTimeString.split(':')[1];

			newMeetingInfo.startDateUtc = moment(formData.startDateString)
				.startOf('day')
				.add(startHours, 'hours')
				.add(startMinutes, 'minutes')
				.utc()
				.toDate();

			newMeetingInfo.endDateUtc = moment(formData.startDateString)
				.startOf('day')
				.add(endHours, 'hours')
				.add(endMinutes, 'minutes')
				.utc()
				.toDate();

			newMeetingInfo.isPlannedRecording = formData.isPlannedRecording;

			if (newMeetingInfo.isPlannedRecording) {
				newMeetingInfo.plannedAgentRegId = formData.plannedAgentRegId;
				newMeetingInfo.plannedAgentSeetings = formData.plannedAgentSeetings;
			}
			
			setIsUploading(true);
			try {
				await onSubmit(newMeetingInfo);
				closeModal();
			} finally {
				setIsUploading(false);
			}
		}

	}, [closeModal, onSubmit, validateForm, getFormData]);

	const headerView = useMemo((): JSX.Element => {
		return (
			<div className="create-meeting-modal__header">
				<ModalTitle onClose={closeModal}>
					Создание собрания
				</ModalTitle>
			</div>
		);
	}, [closeModal]);

	const footerView = useMemo(() => {
		return (
		<Space>
			<Button theme="primary" onClick={handleSubmit} isBusy={isUploading}>
				Загрузить
			</Button>
			<Button onClick={closeModal}>
				Отменить
			</Button>
			<div></div>
		</Space>
		);
	}, [handleSubmit, closeModal, isUploading]);

	const onClearFileOrRecord = useCallback(() => {
		setRecordingFile(null);
		setRecordingFileUri(null);
		filePlayerRef.current.load();


		setIsRecordingFromFile(false);
		setIsRecording(false);
		setIsRecordingPaused(false);
		if (recorder && recorder.state == "recording")
		{
			recorder.stop();
		}
	}, [recorder, setRecordingFile, setRecordingFileUri]);

	const onDrop = useCallback(acceptedFiles => {
		if (acceptedFiles && acceptedFiles.length) {
			setRecordingFile(acceptedFiles[0]);
			setRecordingFileUri(URL.createObjectURL(acceptedFiles[0]));
			setIsRecordingFromFile(true);
            filePlayerRef.current.load();
		}
		const formData = getFormData();
		validateForm(formData);
	}, [setRecordingFile, getFormData, validateForm]);
	
	const {getRootProps, getInputProps, isDragActive, open: openFile} = useDropzone({onDrop, disabled: isUploading, noClick: true});

	const initRecorder = useCallback(async () => {
		const stream = await navigator.mediaDevices.getUserMedia({
			audio: true,
		});
		const audioChunks = [];
		let dataType = "";

		const mediaRecorder = new MediaRecorder(stream);
		mediaRecorder.addEventListener("dataavailable", event => {
			audioChunks.push(event.data);
			dataType = event.data.type;
		});

		const canvas = recordingVisRef.current;
		const canvasCtx = canvas.getContext("2d");
		
		let source: MediaStreamAudioSourceNode = null;

		const visualize = (stream) => {
			const audioCtx = new AudioContext();
			source = audioCtx.createMediaStreamSource(stream);

			const analyser = audioCtx.createAnalyser();
			analyser.fftSize = 2048;
			const bufferLength = analyser.frequencyBinCount;
			const dataArray = new Uint8Array(bufferLength);
			source.connect(analyser);

			const draw = () => {
				const WIDTH = canvas.width
				const HEIGHT = canvas.height;
			
				requestAnimationFrame(draw);
			
				analyser.getByteTimeDomainData(dataArray);
			
				canvasCtx.fillStyle = '#F2F9FF';
				canvasCtx.fillRect(0, 0, WIDTH, HEIGHT);
			
				canvasCtx.lineWidth = 2;
				canvasCtx.strokeStyle = '#16A085';
			
				canvasCtx.beginPath();
			
				const sliceWidth = WIDTH * 1.0 / bufferLength;
				let x = 0;

				for (let i = 0; i < bufferLength; i++) {
					
					const v = dataArray[i] / 128.0;
					const y = v * HEIGHT / 2;

					if(i === 0) {
						canvasCtx.moveTo(x, y);
					} else {
						canvasCtx.lineTo(x, y);
					}

					x += sliceWidth;
				}
			
				canvasCtx.lineTo(canvas.width, canvas.height / 2);
				canvasCtx.stroke();
			}

			draw();
		};

		const onStart = () => {
			visualize(stream);
		};
		mediaRecorder.addEventListener("start", onStart);
		mediaRecorder.onresume = onStart;


		const previewAudioRecord = () => {
			if (source) {
				source.disconnect();
			}

			let defaultExtension = "webm";
			const mediaType = mediaRecorder.mimeType || dataType;
			const mimeMatch = mediaType.match(/audio\/([^;]+)/);
			if (mimeMatch && mimeMatch.length) {
				defaultExtension = mimeMatch[1];
			}
			
			const audioFile = new File(audioChunks, `SumMeetFromBrowser.${defaultExtension}`);
			const audioUrl = URL.createObjectURL(audioFile);

			setRecordingFile(audioFile)
			setRecordingFileUri(audioUrl);
			setIsRecordingFromFile(true);
			filePlayerRef.current.load();
			
			const formData = getFormData();
			validateForm(formData);
		};			
		
		mediaRecorder.addEventListener("stop", previewAudioRecord);
		mediaRecorder.addEventListener("pause", previewAudioRecord);
		return mediaRecorder;
	}, [getFormData, validateForm]);

	const startRecording = useCallback(async () => {
		const initedRecorder = await initRecorder();
		setRecorder(initedRecorder);
		setIsRecording(true);
		setIsRecordingPaused(false);
		initedRecorder.start();

	}, [initRecorder, setIsRecording]);

	const fileDropView = useMemo((): JSX.Element => {
		let dropClasses = 'create-meeting-modal__dropzone create-meeting-modal__dropzone__text';
		if (isDragActive) {
			dropClasses += " dragging";
		}
		return !isRecording && <div {...getRootProps()}>
				<input {...getInputProps()}/>
				{isRecordingFromFile 
				? <div className="create-meeting-modal__dropzone create-meeting-modal__dropzone__file">
					<div className="fileTitle">
						Используется файл {recordingFile.name} <IconButton icon={<DeleteHoverIcon />} title="Очистить" iconSize="24px" onClick={onClearFileOrRecord} />
					</div>
				</div>
				: <div className={dropClasses}>
					<Space className='uploadButtons'>
						<IconButton icon={<UploadIcon />} iconSize='24px' theme='primary' title='Загрузить файл' onClick={openFile} />
						<IconButton icon={<RecordIcon />} iconSize='24px' theme='primary' title='Записать с микрофона' onClick={startRecording} />
					</Space>
					Перетащите сюда файл записи собрания или запишите с микрофона
				</div>}
			</div>;
	}, [getRootProps, getInputProps, isDragActive, isRecordingFromFile, recordingFile,
		onClearFileOrRecord, openFile,startRecording, isRecording]);

	const handleBlur = useCallback((): void => {
		const formData = getFormData();
		validateForm(formData);
	}, [getFormData, validateForm]);

	const pauseRecording = useCallback(() => {
		if (recorder && recorder.state == "recording") {
			recorder.requestData();
			recorder.pause();
		}

		setIsRecording(true);
		setIsRecordingPaused(true);			
	}, [recorder]);

	const resumeRecording = useCallback(() => {
		if (recorder && recorder.state == "paused") {
			recorder.resume();
		} else if (recorder && recorder.state == "inactive") {
			recorder.start();
		}

		setIsRecording(true);
		setIsRecordingPaused(false);			
	}, [recorder]);

	const stopRecording = useCallback(() => {
		if (recorder && recorder.state == "recording") {
			recorder.stop();
		}

		setIsRecording(false);
		setIsRecordingPaused(false);			
	}, [recorder]);

	const recorderClassNames = useClassNames(
		'recording-area',
		!isRecording && '-hidden'
	);


	const recorderView = useMemo(() => {

		return (
		<Space className={recorderClassNames}>
			<canvas ref={recordingVisRef} className="recording-vis">
			</canvas>
			<div className='overlay'>
				<span>{!isRecordingPaused ? "Говорите" : "Продолжить запись"}</span>
				<div className='recordButtons'>
					{!isRecordingPaused && <IconButton className='pauseRecord' icon={<MeetingPauseRecIconModal />} iconSize="24px" onClick={pauseRecording} theme="primary" title="Приостановить" />}
					{isRecordingPaused && <>
						<IconButton icon={<DeleteHoverIcon />} iconSize="24px" onClick={onClearFileOrRecord} theme="secondary" title="Отменить" />
						<IconButton icon={<RecordIcon />} iconSize="24px" onClick={resumeRecording} theme="primary" title="Продолжить запись" />
						<IconButton icon={<AcceptIcon />} iconSize="24px" onClick={stopRecording} theme="secondary" className='svg-nofill' title="Закончить запись" />
					</>}
				</div>
			</div>
		</Space>)
	}, [isRecordingPaused, onClearFileOrRecord, pauseRecording, resumeRecording, stopRecording, recorderClassNames]);

	return (
		<Modal
			className="create-meeting-modal"
			footer={footerView}
			header={headerView}
			isOpen
		>
			<div className="create-meeting-modal__content">
				<FormItem label="Название" validationMessages={valMessages['subject']}>
					<Textbox
						value={meetingSubject}
						onValueChange={setMeetingSubject}
						onBlur={handleBlur}
						disabled={isUploading}
					/>
				</FormItem>
				<Space>
					<FormItem label="Дата проведения" validationMessages={valMessages['startDateString']}>
						<DayPickerInput
							value={meetingStartDate}
							onBlur={handleBlur}
							onDayChange={setMeetingStartDate}
							disabled={isUploading}
						/>
					</FormItem>
					{/* {endTimeString} */}
					<FormItem label="Время проведения" validationMessages={valMessages['startAndEndTimes']}> 
						<Space>
							<Textbox
								type='time'
								value={meetingStartTime}
								onValueChange={setMeetingStartTime}
								className="time"
								onBlur={handleBlur}
								disabled={isUploading}
							/>
							<div className="time-interval">—</div>
							<Textbox
								type='time'
								value={meetingEndTime}
								onValueChange={setMeetingEndTime}
								className="time"
								onBlur={handleBlur}
								disabled={isUploading}
							/>
						</Space>
					</FormItem>
				</Space>
					<FormItem label="Место проведения" validationMessages={valMessages['location']}>
						<Textbox
							value={meetingLocation}
							onValueChange={setMeetingLocation}
							onBlur={handleBlur}
							disabled={isUploading}
						/>
					</FormItem>
					<FormItem validationMessages={valMessages['recordingFileAndIsPlanned']}>
						{fileDropView}
						{recorderView}
					</FormItem>
					<FormItem>
						{<Player
							disabled={!recordingFileUri}
							playerRef={filePlayerRef}
							mediaUrl={recordingFileUri}
							onIsPlayingChanged={() => true}
							onTimeChanged={() => true} 
						/>}
						<Space>
					</Space>
					</FormItem>
			</div>
		</Modal>
	);
}

export {CreateMeetingModal};

